/* eslint-disable flowtype/no-weak-types */
// @flow
import { get, set, castArray } from 'lodash';
import URI from 'urijs';
import lz from 'lz-string';
import moment from 'moment';
import aes from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';

import { windowWrapper, appQueryParameters } from '@clubmed/utils';

import { CXO_STEP } from 'Views/booking/constants';
import gqlClient from 'Utils/graphql-client/graphqlClient';
import * as pleUtils from 'Utils/ple-utils';
import { PROPOSAL_SAVE } from 'Utils/request/requestDictionary';
import * as constants from './constants';

const CXO_CRYPTO_PASSWORD = 'clubmed-cxo';

export type Traveler = {
  neolid: string,
  isMainContact: boolean,
  isChild: boolean,
  birthdate: ?timestamp,
};

function cxoDateToTimestamp(cxoDate: ?string): ?timestamp {
  return cxoDate ? moment(cxoDate, constants.CXO_DATE_FORMAT).valueOf() : undefined;
}

function _getQueryParametersFromCurrentUri(): ?{} {
  const location = windowWrapper.getLocation();
  return location ? URI.parseQuery(new URI(location).query()) : null;
}

function _getQueryParametersAndValuesFromCurrentUri(): ?string {
  const location = windowWrapper.getLocation();
  return location ? new URI(location).search() : null;
}

function _getCxoUrl(): string {
  return URI.decode(get(_getQueryParametersFromCurrentUri(), appQueryParameters.cxo.cxo_url));
}

function _performPostMessage(functionToCall: string, params: Array<any>): ?Promise<*> {
  const parent = windowWrapper.get('opener') || windowWrapper.get('parent');
  if (parent) {
    const origin = _getCxoUrl();
    return new Promise((resolve, reject) => {
      try {
        parent.postMessage([functionToCall, ...params], origin);
        resolve();
      } catch (e) {
        reject();
      }
    });
  }
}

function _getQueryParam(queryParam: string): ?string {
  return get(_getQueryParametersFromCurrentUri(), queryParam);
}

function isSeller(appContext: AppContext): boolean {
  return !!(appContext.isDirectCxo || appContext.isCmta || appContext.isIndirectCxo);
}

function getOpportunityGUID(queryParams: QueryParams): ?string {
  // $FlowFixMe some queryParams are arrays
  return queryParams[appQueryParameters.cxo.cxo_opportunity_guid];
}

function _parseCxoTraveler(cxoTraveler: string, primaryContactNeolid: ?string): Traveler {
  // cxo_traveler can be an adult with syntax: 'neolid' or a child: 'neolid,birthdate'
  const travelerInfo = cxoTraveler.split(',');
  return {
    neolid: travelerInfo[0],
    isMainContact: travelerInfo[0] === primaryContactNeolid,
    isChild: !!travelerInfo[1],
    birthdate: cxoDateToTimestamp(travelerInfo[1]),
  };
}

function getCxoTravelers(): ?Array<Traveler> {
  const cxoTravelers = _getQueryParam(appQueryParameters.cxo.cxo_traveler);
  const primaryContactNeolid = _getQueryParam(appQueryParameters.cxo.cxo_primarycontact);

  if (!cxoTravelers) return null;

  const travelers = castArray(cxoTravelers).map(traveler =>
    _parseCxoTraveler(traveler, primaryContactNeolid),
  );

  // If there is no main contact in the travelers it defaults to the first adult
  if (!travelers.some(traveler => traveler.isMainContact)) {
    set(travelers.find(traveler => !traveler.isChild), 'isMainContact', true);
  }

  // The main contact is put first in the array to avoid deletion on later participant change
  travelers.sort((a, b) => {
    if (a.isMainContact) return -1;
    return b.isMainContact ? 1 : 0;
  });

  return travelers;
}

function getNeolids(): Array<string> {
  return (getCxoTravelers() || []).map(traveler => traveler.neolid);
}

function saveCustomerProposal(
  proposalId: string,
  appContext: AppContext,
  customerId: string,
): Promise<*> {
  if (!customerId) {
    return Promise.reject();
  }
  const { cmsContent, cmsRelease } = appContext;
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      saveCustomerProposals(
        customerId : "${customerId}",
        proposalId: "${proposalId}") {
          errorDescription (content:"${cmsContent || ''}" release:"${cmsRelease || ''}")
          errorKey
      }
    }`,
    appContext,
    { requestName: PROPOSAL_SAVE },
  );
}

const replaceWebNaUrlPlaceholderBy = (value: string) =>
  'https://{placeholder}/CxOInstant.jsp'.replace('{placeholder}', value);

const getWebNaBaseURL = () => {
  if (window.location.origin.includes('staging.clubmed')) {
    return replaceWebNaUrlPlaceholderBy('staging.webna.clubmed.com/smoothNA');
  }

  if (window.location.origin.includes('www.clubmed')) {
    return replaceWebNaUrlPlaceholderBy('webna.clubmed.com/smoothNA');
  }

  return replaceWebNaUrlPlaceholderBy('narec/smoothNA');
};

const getQueryStringNeolIds = proposal =>
  proposal.attendees.list.reduce(
    (acc, { customerId }) => acc + '&cxo_neolid=' + (customerId || ''),
    '',
  );

function redirectNewTabToNA({
  newTab,
  step,
  context,
  opportunityId,
  proposal,
  location,
}: {
  newTab: *,
  step: string,
  context: string,
  opportunityId: ?string,
  appContext: AppContext,
  proposal: *,
  location: BookingRouterLocation,
}) {
  newTab.location.href = `${getWebNaBaseURL()}?cxo_opportunity_guid=${opportunityId ||
    ''}&cxo_sales_network_id=${location.queryParams.cxo_sales_network_id ||
    ''}&cxo_step=${step}&cxo_context=${context}${getQueryStringNeolIds(proposal)}&init_na=${location
    .queryParams.init_na || ''}`;
}

function sendFacetsToCxO(facets: Array<*>) {
  _performPostMessage(constants.CXO_FACETS_FUNC_TO_CALL, facets);
}

function getCurrentQueryParamsAndState(proposalId: ?string, step: ?string): {} {
  const currentQueryParams = URI.parseQuery(_getQueryParametersAndValuesFromCurrentUri());
  if (proposalId && step) {
    const state = lz.compressToEncodedURIComponent(JSON.stringify({ proposalId, step }));
    return {
      ...currentQueryParams,
      state,
    };
  }
  return currentQueryParams;
}

function getSigninURIWithRedirection({
  locale,
  proposalId,
  step,
  isCmta,
}: {
  locale: string,
  proposalId: ?string,
  step: ?string,
  isCmta: boolean,
}): string {
  const redirectUri = new URI(windowWrapper.getLocation())
    .query(getCurrentQueryParamsAndState(proposalId, step))
    .toString();

  return pleUtils.buildSellerSigninUrl({ redirectUri, locale, isCmta });
}

function createCxoProject(
  proposalId: string,
  appContext: AppContext,
  location: BookingRouterLocation,
  hasCheckedOpt: boolean,
) {
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      bookings(
        proposalId: "${proposalId}"
        opportunityId: "${location.queryParams.cxo_opportunity_guid || ''}"
        bookingStatus: OPTION
        sendingOptionInformation: ${hasCheckedOpt.toString()}
      ) {
        bookingId
      }
    }`,
    appContext,
    { requestName: PROPOSAL_SAVE },
  );
}

async function saveCustomerProposalAndSendContextToCxO({
  proposalId,
  step,
  appContext,
  proposal,
  location,
}: {
  proposalId: string,
  step: string,
  appContext: AppContext,
  proposal: Proposal,
  location: BookingRouterLocation,
}): * {
  const hasCheckedOption = step === CXO_STEP.AFFICHAGE;
  const cxoOpportunityId = getOpportunityGUID(location.queryParams);
  let cxoProjectId = proposalId;
  let newTab;

  if (step !== CXO_STEP.AFFICHAGE) {
    // open a new tab now to not be blocked by web browser
    newTab = window.open();
  }

  try {
    if (step === CXO_STEP.AFFICHAGE || step === CXO_STEP.PAYMENT) {
      cxoProjectId = (await createCxoProject(proposalId, appContext, location, hasCheckedOption))
        .bookings.bookingId;
    }

    await saveCustomerProposal(
      proposalId,
      appContext,
      get(proposal.attendees.list, '[0].customerId'),
    );

    if (step !== CXO_STEP.AFFICHAGE && newTab) {
      redirectNewTabToNA({
        newTab,
        step,
        appContext,
        proposal,
        opportunityId: cxoOpportunityId,
        context: cxoProjectId,
        location,
      });
    }

    return {
      bookingId: cxoProjectId,
    };
  } catch (err) {
    if (newTab) {
      newTab.close();
    }

    if (pleUtils.hasAuthResponseError(err)) {
      windowWrapper.setLocation(
        getSigninURIWithRedirection({
          locale: appContext.locale,
          proposalId,
          step,
          isCmta: appContext.isCmta,
        }),
      );
    }

    throw err;
  }
}

const handle401GraphqlErrorSeller = (errors: *, appContext: AppContext) => {
  if (get(errors, 'errors.[0].statusCode') === 401) {
    window.location.href = getSigninURIWithRedirection({
      locale: appContext.locale,
      proposalId: null,
      step: null,
      isCmta: appContext.isCmta,
    });
  }
};

const decryptHeptaSeller = (cxoId: string) =>
  aes.decrypt(cxoId.replace(/\s/g, '+'), CXO_CRYPTO_PASSWORD).toString(enc);

const isSellerAndInMultiplesFamiliesContext = (
  appContext: AppContext,
  attendees: Participants,
): boolean => {
  return (
    (appContext.isDirectCxo || appContext.isIndirectCxo) &&
    attendees.some(({ gmNumber }) => !!gmNumber && gmNumber !== attendees[0].gmNumber)
  );
};

export {
  getOpportunityGUID,
  getCxoTravelers,
  getNeolids,
  isSeller,
  redirectNewTabToNA,
  sendFacetsToCxO,
  saveCustomerProposal,
  getCurrentQueryParamsAndState,
  getSigninURIWithRedirection,
  saveCustomerProposalAndSendContextToCxO,
  handle401GraphqlErrorSeller,
  decryptHeptaSeller,
  isSellerAndInMultiplesFamiliesContext,
};
